/**
 * ExpenseCard — affichage compact d'une dépense immeuble.
 * Extrait de DocsAndExpensesPanel.jsx (Batch #14.221).
 */
import { Trash2, Loader2, Clock, Wallet, Receipt,
  Hammer, Zap, Droplets, Snowflake, Bug, ShieldCheck,
  Banknote, Briefcase, Calculator, CreditCard, Image as ImageIcon, Building2 } from "lucide-react";

const EXPENSE_CATEGORY_ICONS = {
  entretien_reparation:    Hammer,
  renovations:             Hammer,
  taxes_municipales:       Receipt,
  taxes_scolaires:         Receipt,
  assurance_immeuble:      ShieldCheck,
  assurance_responsabilite: ShieldCheck,
  energie_chauffage:       Zap,
  electricite_communes:    Zap,
  eau_aqueduc:             Droplets,
  internet_telecom:        Zap,
  concierge_menage:        Briefcase,
  deneigement_paysagement: Snowflake,
  extermination:           Bug,
  frais_juridiques:        Briefcase,
  frais_comptables:        Calculator,
  frais_administration:    Briefcase,
  frais_bancaires_stripe:  CreditCard,
  interets_hypotheque:     Banknote,
  publicite_marketing:     ImageIcon,
  petits_outils:           Hammer,
  fournitures_bureau:      Briefcase,
  autre:                   Wallet,
};

const EXPENSE_CATEGORIES = [
  { value: "entretien_reparation",    label: "Entretien / Réparation" },
  { value: "renovations",              label: "Rénovations" },
  { value: "taxes_municipales",        label: "Taxes municipales" },
  { value: "taxes_scolaires",          label: "Taxes scolaires" },
  { value: "assurance_immeuble",       label: "Assurance immeuble" },
  { value: "assurance_responsabilite", label: "Assurance responsabilité" },
  { value: "energie_chauffage",        label: "Énergie / Chauffage" },
  { value: "electricite_communes",     label: "Électricité communes" },
  { value: "eau_aqueduc",              label: "Eau / Aqueduc" },
  { value: "internet_telecom",         label: "Internet / Télécom" },
  { value: "concierge_menage",         label: "Concierge / Ménage" },
  { value: "deneigement_paysagement",  label: "Déneigement / Paysagement" },
  { value: "extermination",            label: "Extermination" },
  { value: "frais_juridiques",         label: "Frais juridiques" },
  { value: "frais_comptables",         label: "Frais comptables" },
  { value: "frais_administration",     label: "Frais d'administration" },
  { value: "frais_bancaires_stripe",   label: "Frais bancaires / Stripe" },
  { value: "interets_hypotheque",      label: "Intérêts hypothèque" },
  { value: "publicite_marketing",      label: "Publicité / Marketing" },
  { value: "petits_outils",            label: "Petits outils" },
  { value: "fournitures_bureau",       label: "Fournitures bureau" },
  { value: "autre",                    label: "Autre" },
];

function ExpenseCard({ exp, busy, onDelete, fmtDate }) {
  const Icon = EXPENSE_CATEGORY_ICONS[exp.category] || Wallet;
  const categoryLabel = EXPENSE_CATEGORIES.find((c) => c.value === exp.category)?.label || exp.category;
  return (
    <div
      className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-rose-100 bg-rose-50/40 hover:border-rose-300 hover:shadow-sm transition"
      data-testid={`docs-expenses-card-expense-${exp.id}`}
    >
      <div className="w-11 h-11 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-slate-800 truncate">{exp.vendor || "Fournisseur —"}</span>
          {exp.invoice_number && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 font-mono">
              N° {exp.invoice_number}
            </span>
          )}
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold">
            Dépense · {categoryLabel}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span><strong>{exp._master_name || "—"}</strong></span>
          {exp._building_name && (
            <span className="inline-flex items-center gap-1">
              <Building2 className="w-3 h-3" />{exp._building_name}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {fmtDate(exp.invoice_date)}
          </span>
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono font-bold text-rose-800 text-sm">{Number(exp.amount_total).toFixed(2)} $</div>
        <div className="text-[10px] text-slate-500 font-mono">
          {Number(exp.amount_gross).toFixed(2)} + {(Number(exp.tps_amount || 0) + Number(exp.tvq_amount || 0)).toFixed(2)} tx
        </div>
      </div>
      <button
        onClick={onDelete}
        disabled={busy}
        className="p-1.5 rounded-lg bg-white hover:bg-rose-100 text-rose-600 border border-rose-200 disabled:opacity-40"
        title="Archiver"
        data-testid={`docs-expenses-delete-expense-${exp.id}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export { ExpenseCard };
